import { getAdminUsers } from '@selectors/adminUsers';
import dayjs from 'dayjs';
import React, { CSSProperties, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { CalendarChallenge } from 'src/types/shared/Challenge';

const CalendarGanttBar: React.FC<CalendarGanttBarProps> = ({
    id,
    stage,
    barStartDate,
    barEndDate,
    calendarStartDate,
    calendarEndDate,
    color,
    challenge,
    isExpanded = true,
}) => {
    const startDatesAreTheSame = dayjs(barStartDate).isSame(dayjs(calendarStartDate), 'day');
    const endDatesAreTheSame = dayjs(barEndDate).isSame(dayjs(calendarEndDate), 'day');
    const startIsOutOfRange = dayjs(barStartDate).isBefore(dayjs(calendarStartDate));
    const endIsOutOfRange = dayjs(barEndDate).isAfter(dayjs(calendarEndDate)) || false;
    let barIsOutOfRange = false;
    if (
        dayjs(barStartDate).isAfter(dayjs(calendarEndDate)) ||
        dayjs(barEndDate).isBefore(dayjs(calendarStartDate))
        // (dayjs(barStartDate).isBefore(dayjs(calendarStartDate)) && !barEndDate)
    ) {
        barIsOutOfRange = true;
    } // Does bar fall entirely outside of calendar range?
    if (startIsOutOfRange && !barEndDate) barIsOutOfRange = true; // Does bar start before calendar start date and end date is not defined?

    const adminUsers = useSelector(getAdminUsers);

    const challengeLead = adminUsers[challenge.challengeLeadID];

    const tooltipContent: Record<string, string[]> = {
        workshop: [
            `${challenge.name}`,
            `Workshop - ${dayjs(challenge.workshopDate).format('Do MMMM YYYY')}`,
            `${challenge.workshopAdminIDs
                .map((id, i) => `${adminUsers[id]?.firstName} ${adminUsers[id]?.lastName}` || null)
                .filter(item => !!item)
                .join(', ')}`,
        ],
        livechallenge: [
            `${challenge.name}`,
            `Live challenge - ${dayjs(challenge.startDate).format('Do MMMM YYYY')} - ${dayjs(
                challenge.endDate,
            ).format('Do MMMM YYYY')}`,
            `${
                challenge.challengeLeadID
                    ? `${challengeLead?.firstName} ${challengeLead?.lastName}`
                    : ''
            }`,
        ],
        assessment: [
            `${challenge.name}`,
            `Assessment - ${dayjs(challenge.assessmentStartDate).format('Do MMMM YYYY')} - ${dayjs(
                challenge.responseDate,
            ).format('Do MMMM YYYY')}`,
            `${challenge.workshopAdminIDs
                .map((id, i) => `${adminUsers[id]?.firstName} ${adminUsers[id]?.lastName}` || null)
                .filter(item => !!item)
                .join(', ')}`,
        ],

        pitch: [
            `${challenge.name}`,
            `Pitch - ${dayjs(challenge.pitchPresentationDate).format('Do MMMM YYYY')}`,
            `${challenge.pitchPresentationAdminIDs
                .map((id, i) => `${adminUsers[id]?.firstName} ${adminUsers[id]?.lastName}` || null)
                .filter(item => !!item)
                .join(', ')}`,
        ],
    };

    const [tooltipPosition, setTooltipPosition] = useState<Record<string, boolean>>({
        isVisible: false,
        isBelow: false,
        isToLeft: false,
        isToRight: false,
    });

    const sD = {
        y: dayjs(barStartDate).year(),
        m: String(dayjs(barStartDate).month() + 1).padStart(2, '0'),
        d: String(dayjs(barStartDate).date()).padStart(2, '0'),
    };
    const eD = barEndDate
        ? {
              y: dayjs(barEndDate).year(),
              m: String(dayjs(barEndDate).month() + 1).padStart(2, '0'),
              d: String(dayjs(barEndDate).date()).padStart(2, '0'),
          }
        : { ...sD };

    const gridRowStart = isExpanded ? `challenge-${id}-${stage}-start` : `challenge-${id}-start`;
    const gridRowEnd = isExpanded ? `challenge-${id}-${stage}-end` : `challenge-${id}-end`;
    const gridColumnStart =
        startIsOutOfRange || startDatesAreTheSame ? 'sidebar-end' : `start-${sD.y}-${sD.m}-${sD.d}`;
    const gridColumnEnd =
        endIsOutOfRange || endDatesAreTheSame ? 'cols-end' : `end-${eD.y}-${eD.m}-${eD.d}`;

    const spanBarStyles: CSSProperties = {
        gridArea: `${gridRowStart} / ${gridColumnStart} / ${gridRowEnd} / ${gridColumnEnd}`,
        backgroundColor: color,
        transform: !barEndDate ? 'rotate(45deg) scale(80%)' : 'rotate(0deg) scale(100%)',
    };
    if (!barEndDate) spanBarStyles.justifySelf = 'center';

    const tooltipContainerStyles: CSSProperties = {
        transform: !barEndDate ? 'rotate(-45deg)' : 'unset',
        left: !barEndDate ? `calc(50% ${tooltipPosition.isBelow ? '+' : '-'} 1.5em)` : '50%',
        zIndex: tooltipPosition.isVisible ? 100 : 'unset',
    };

    const tooltipStyles: CSSProperties = {
        minWidth: '200px',
    };

    const handleShowTooltip = (e: any) => {
        if (!isExpanded) return;
        const ganttContainer = document.getElementById('gantt-container');
        const bounds = ganttContainer?.getBoundingClientRect();
        const newPosition = {} as Record<string, boolean>;
        if (!!bounds) {
            const x = e.clientX - bounds.left;
            const y = e.clientY - bounds.top;
            if (y < 200) newPosition.isBelow = true;
            if (x < 400) newPosition.isToRight = true;
            else if (x > bounds.width - 150) newPosition.isToLeft = true;
        }

        setTooltipPosition(prevState => ({ ...prevState, isVisible: true, ...newPosition }));
    };

    const handleHideTooltip = (e: MouseEvent<HTMLDivElement>) => {
        setTooltipPosition({
            isVisible: false,
            isBelow: false,
            isToLeft: false,
            isToRight: false,
        });
    };

    if (barIsOutOfRange) return <></>;

    return (
        <div
            className="challenge-gantt-bar"
            style={spanBarStyles}
            onMouseOver={handleShowTooltip}
            onMouseLeave={handleHideTooltip}
        >
            {tooltipPosition.isVisible && (
                <div
                    className={`tooltip-container ${tooltipPosition.isBelow ? 'below' : ''}`}
                    style={tooltipContainerStyles}
                >
                    <div
                        className={`tooltip ${tooltipPosition.isBelow ? 'below' : ''} ${
                            tooltipPosition.isToLeft
                                ? 'left'
                                : tooltipPosition.isToRight
                                ? 'right'
                                : ''
                        }`}
                        style={tooltipStyles}
                    >
                        {tooltipContent[stage].join('\n')}
                        <span
                            className={`tooltip-pointer ${tooltipPosition.isBelow ? 'top' : ''} ${
                                tooltipPosition.isToLeft
                                    ? 'left'
                                    : tooltipPosition.isToRight
                                    ? 'right'
                                    : ''
                            }`}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

interface CalendarGanttBarProps {
    id: number;
    stage: string;
    barStartDate: Date | string;
    barEndDate?: Date | string; // Optional - can be point, not range
    calendarStartDate: Date | string;
    calendarEndDate: Date | string;
    color?: string;
    challenge: CalendarChallenge;
    isExpanded?: boolean;
}

export default CalendarGanttBar;
